"use client";

import { useMemo } from "react";

import type { BreadcrumbItemData } from "@/components/breadcrumb-types";
import {
  ResponsiveBreadcrumb,
  type ResponsiveBreadcrumbItem,
} from "@gogive/design-system/breadcrumb";
import { cn } from "@/lib/utils";

export type PageLayoutBreadcrumbsProps = {
  className?: string;
  items: BreadcrumbItemData[];
};

export function PageLayoutBreadcrumbs({
  className,
  items,
}: PageLayoutBreadcrumbsProps) {
  const responsiveItems = useMemo(
    () =>
      items.reduce<ResponsiveBreadcrumbItem[]>((acc, item, index) => {
        const isLastItem = index === items.length - 1;
        if (!item.href && !isLastItem) return acc;

        const responsiveItem = {
          id: item.href ?? `${item.label}-${index}`,
          label: item.label,
          ...(item.iconName ? { iconName: item.iconName } : {}),
        };

        if (!item.href) {
          acc.push(responsiveItem);
          return acc;
        }

        acc.push({
          ...responsiveItem,
          href: item.href,
        });

        return acc;
      }, []),
    [items],
  );

  if (items.length === 0) return null;

  return (
    <div className={cn("flex min-w-0 shrink items-center", className)}>
      <ResponsiveBreadcrumb
        aria-label="Breadcrumb"
        className="min-w-0"
        collapsedItemsLabel="More"
        items={responsiveItems}
      />
    </div>
  );
}
